import { map } from 'rxjs/operators';
import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { Observable, BehaviorSubject } from 'rxjs';

import { StorageMap } from '@ngx-pwa/local-storage';
import { CookieService } from 'ngx-cookie-service';

export interface ISiteValue<T> {
    site: string;
    value: T;
}

@Injectable()
export class StorageAndCookieService {
    private _outputMap: Map<string, { value: BehaviorSubject<any>; realValue: any; customEvent: any }> = new Map();

    private _initMap: Map<string, any> = new Map();

    private _prefix = 'EVENTSTORAGE_';

    constructor(public localStorage: StorageMap, public cookieservice: CookieService, @Inject(DOCUMENT) private _document) {}

    // must be used in constructor
    init<T>(key: string, initValue: T) {
        if (!this._outputMap.has(key)) {
            this._initMap.set(key, JSON.parse(JSON.stringify(initValue)));
            this._initOutputMap(key, initValue);
            const _sub = this.localStorage.get(key).subscribe((data: T) => {
                if (!this.cookieservice.check(key) || data === null) {
                    this.set<T>(key, initValue);
                    this._initOutputMap(key, initValue);
                } else {
                    this._initOutputMap(key, data);
                }
                _sub.unsubscribe();
            });
        }
    }

    // must be used in constructor
    initSite<T>(site: string, key: string, initValue: T) {
        const _preinit: Array<ISiteValue<T>> = [{ site: site, value: initValue }];
        if (!this._outputMap.has(key)) {
            this._initMap.set(key, JSON.parse(JSON.stringify(_preinit)));
            this._initOutputMap<Array<ISiteValue<T>>>(key, _preinit);
            const _sub = this.localStorage.get(key).subscribe((data: Array<ISiteValue<T>>) => {
                if (data == null || !this.cookieservice.check(key)) {
                    this.set<Array<ISiteValue<T>>>(key, _preinit);
                } else {
                    const _index = data.findIndex((item) => item.site === site);

                    if (_index === -1) {
                        data.push(_preinit[0]);
                        this.set<Array<ISiteValue<T>>>(key, data);
                        this._initOutputMap<Array<ISiteValue<T>>>(key, [...data]);
                    } else {
                        this._initOutputMap<Array<ISiteValue<T>>>(key, data);
                    }
                }

                _sub.unsubscribe();
            });
        }
    }

    has(key): Observable<boolean> {
        return this._outputMap.get(key).value.asObservable();
    }

    get<T>(key: string): T {
        return this._outputMap.get(key).realValue;
    }

    getAsync<T>(key: string): Observable<T> {
        return this._outputMap.get(key).value.asObservable();
    }

    getSiteAsync<T>(site: string, key: string): Observable<T> {
        return this._outputMap.get(key).value.pipe(
            map((dataArray: any) => {
                if (dataArray) {
                    const _object: any = <Array<ISiteValue<T>>>dataArray.find((item) => item.site === site);
                    return _object != null ? _object.value : [];
                }
                return [];
            })
        );
        // return this._outputMap.get(key).value.asObservable();
    }

    set<T>(key: string, value: any) {
        this._setCookie(key);
        this.localStorage.set(key, value).subscribe();
        this._sendEvent(key);
    }

    setAsync<T>(key: string, value: T): Observable<boolean> {
        this._setCookie(key);

        return this.localStorage.set(key, value).pipe((data) => {
            this._sendEvent(key);
            return data;
        });
    }

    setSiteAsync<T>(site: string, key: string, value: T): Observable<boolean> {
        const data = JSON.parse(JSON.stringify(this.get<Array<ISiteValue<T>>>(key)));
        this._setCookie(key);
        if (data) {
            const _index = data.findIndex((item) => item.site === site);
            if (_index > -1) {
                data[_index].value = value;
            }
            return this.localStorage.set(key, data).pipe((retData) => {
                this._sendEvent(key);
                return retData;
            });
        } else {
            return this.localStorage.set(key, value).pipe((retData) => {
                this._sendEvent(key);
                return retData;
            });
        }
    }

    delete(key: string) {
        this.cookieservice.delete(key);
        this.localStorage.delete(key).subscribe((data) => {
            if (data) {
                const _outputValue = this._outputMap.get(key);
                const _initValue = JSON.parse(JSON.stringify(this._initMap.get(key)));
                _outputValue.realValue = JSON.parse(JSON.stringify(_initValue));
                _outputValue.value.next(_initValue);
            }
        });
    }

    reload<T>(key: string) {
        const _value = this._outputMap.get(key).value;
        const _sub = this.localStorage.get(key).subscribe((change: T) => {
            if (change != null) {
                const _dataValue = JSON.parse(JSON.stringify(change));
                this._outputMap.get(key).realValue = JSON.parse(JSON.stringify(_dataValue));
                _value.next(_dataValue);
            }
            _sub.unsubscribe();
        });
    }

    private _organizeCookie(key: string): void {
        const _check = this.cookieservice.check(key);
        if (!_check) {
            this.delete(key);
        }
    }

    private _initOutputMap<T>(key, initValue: any) {
        if (!this._outputMap.has(key)) {
            const _value = new BehaviorSubject(initValue);
            this._outputMap.set(key, {
                value: _value,
                realValue: null,
                customEvent: this._document.addEventListener(`${this._prefix}${key.toUpperCase()}`, () => {
                    this.localStorage
                        .get(key)
                        .toPromise()
                        .then((data: T) => {
                            if (data != null) {
                                this._outputMap.get(key).realValue = JSON.parse(JSON.stringify(data));
                                _value.next(data);
                            }
                        });
                })
            });
            this.reload<any>(key);
        } else {
            this._outputMap.get(key).realValue = JSON.parse(JSON.stringify(initValue));
            this._outputMap.get(key).value.next(initValue);
        }
    }

    private _sendEvent(key: string) {
        const event = document.createEvent('Event');
        event.initEvent('storageAndCookie-change', true, true);
        document.dispatchEvent(event);
    }

    private _setCookie(key) {
        this._organizeCookie(key);
        this.cookieservice.set(key, 'true', 365, '/');
        // send custom Event
        const event = this._document.createEvent(`Event`);
        event.initEvent(`${this._prefix}${key.toUpperCase()}`, true, true);
        this._document.dispatchEvent(event);
    }
}
